import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Select, Button, message, Checkbox, Space, Card, Radio } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';

const { Option } = Select;
const { TextArea } = Input;

type CommunicationType = 'CONTRACTS' | 'BOOKING_REQUESTS' | 'SHIPPING_INSTRUCTIONS' | 'SHIPPING_DOCS';
type RecipientType = 'TO' | 'CC';

interface CommunicationPreference {
  communicationType: CommunicationType;
  recipientType: RecipientType;
  enabled: boolean;
}

interface Contact {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  title?: string;
  department?: string;
  isPrimary: boolean;
  communicationPreferences?: CommunicationPreference[];
}

interface Company {
  id: string;
  name: string;
  shortCode: string;
  shortName?: string;
  type: 'SUPPLIER' | 'BUYER' | 'BOTH';
  address?: string;
  country?: string;
  status: 'ACTIVE' | 'INACTIVE';
  contacts?: Contact[];
  paymentMethod?: 'CHECK' | 'WIRE' | 'ACH' | 'WITHHOLD';
  needsCommissionInvoice?: boolean;
  commissionWithheldFromPayment?: boolean;
}

interface CompanyManagementModalProps {
  visible: boolean;
  onClose: () => void;
  mode: 'create' | 'edit';
  company?: Company | null;
}

const countries = [
  'United States', 'Japan', 'Canada', 'Mexico', 'United Kingdom', 
  'Germany', 'France', 'Italy', 'Spain', 'Netherlands', 'Belgium',
  'China', 'South Korea', 'Australia', 'Brazil', 'Argentina',
  'Chile', 'Peru', 'India', 'Thailand', 'Vietnam', 'Other'
];

export const CompanyManagementModal: React.FC<CompanyManagementModalProps> = ({
  visible,
  onClose,
  mode,
  company
}) => {
  const [form] = Form.useForm();
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (visible) {
      if (mode === 'edit' && company) {
        // Map all contacts from company
        const contacts = company.contacts?.map(contact => {
          // Convert communication preferences array to flat form fields
          const prefs = contact.communicationPreferences || [];
          const contractPref = prefs.find(p => p.communicationType === 'CONTRACTS');
          const siPref = prefs.find(p => p.communicationType === 'SHIPPING_INSTRUCTIONS');
          const bookingPref = prefs.find(p => p.communicationType === 'BOOKING_REQUESTS');
          const docsPref = prefs.find(p => p.communicationType === 'SHIPPING_DOCS');

          return {
            id: contact.id,
            firstName: contact.firstName,
            lastName: contact.lastName,
            email: contact.email,
            phone: contact.phone || '',
            title: contact.title || '',
            isPrimary: contact.isPrimary,
            // Communication preferences as flat fields
            receiveContracts: contractPref?.enabled || false,
            contractsRecipientType: contractPref?.recipientType || 'TO',
            receiveShippingInstructions: siPref?.enabled || false,
            siRecipientType: siPref?.recipientType || 'TO',
            receiveBookingRequests: bookingPref?.enabled || false,
            bookingRecipientType: bookingPref?.recipientType || 'TO',
            receiveShippingDocs: docsPref?.enabled || false,
            docsRecipientType: docsPref?.recipientType || 'TO',
          };
        }) || [];

        form.setFieldsValue({
          name: company.name,
          shortCode: company.shortCode,
          shortName: company.shortName || '',
          type: company.type,
          address: company.address || '',
          country: company.country || '',
          status: company.status,
          paymentMethod: company.paymentMethod || 'CHECK',
          needsCommissionInvoice: company.needsCommissionInvoice ?? true,
          commissionWithheldFromPayment: company.commissionWithheldFromPayment ?? false,
          contacts: contacts.length > 0 ? contacts : [{
            firstName: '',
            lastName: '',
            email: '',
            phone: '',
            title: '',
            isPrimary: true,
            receiveContracts: false,
            contractsRecipientType: 'TO',
            receiveShippingInstructions: false,
            siRecipientType: 'TO',
            receiveBookingRequests: false,
            bookingRecipientType: 'TO',
            receiveShippingDocs: false,
            docsRecipientType: 'TO',
          }],
        });
      } else {
        form.resetFields();
        form.setFieldsValue({
          type: 'SUPPLIER',
          status: 'ACTIVE',
          country: 'United States',
          paymentMethod: 'CHECK',
          needsCommissionInvoice: true,
          commissionWithheldFromPayment: false,
          contacts: [{
            firstName: '',
            lastName: '',
            email: '',
            phone: '',
            title: '',
            isPrimary: true,
            receiveContracts: false,
            contractsRecipientType: 'TO',
            receiveShippingInstructions: false,
            siRecipientType: 'TO',
            receiveBookingRequests: false,
            bookingRecipientType: 'TO',
            receiveShippingDocs: false,
            docsRecipientType: 'TO',
          }],
        });
      }
    }
  }, [visible, mode, company, form]);

  const handleSubmit = async (values: any) => {
    setIsSubmitting(true);
    try {
      const endpoint = mode === 'edit'
        ? `/api/v1/companies/${company?.id}`
        : '/api/v1/companies';

      const method = mode === 'edit' ? 'PUT' : 'POST';

      // Validate at least one contact exists
      if (!values.contacts || values.contacts.length === 0) {
        message.error('At least one contact is required');
        setIsSubmitting(false);
        return;
      }

      // Ensure at least one contact is primary
      const hasPrimary = values.contacts.some((c: any) => c.isPrimary);
      if (!hasPrimary && values.contacts.length > 0) {
        values.contacts[0].isPrimary = true;
      }

      // Map contacts and filter out incomplete ones
      const contacts = values.contacts
        .filter((c: any) => c.firstName && c.lastName && c.email)
        .map((contact: any) => {
          // Convert flat form fields to communication preferences array
          const communicationPreferences: CommunicationPreference[] = [];

          if (contact.receiveContracts) {
            communicationPreferences.push({
              communicationType: 'CONTRACTS',
              recipientType: contact.contractsRecipientType || 'TO',
              enabled: true,
            });
          }

          if (contact.receiveShippingInstructions) {
            communicationPreferences.push({
              communicationType: 'SHIPPING_INSTRUCTIONS',
              recipientType: contact.siRecipientType || 'TO',
              enabled: true,
            });
          }

          if (contact.receiveBookingRequests) {
            communicationPreferences.push({
              communicationType: 'BOOKING_REQUESTS',
              recipientType: contact.bookingRecipientType || 'TO',
              enabled: true,
            });
          }

          if (contact.receiveShippingDocs) {
            communicationPreferences.push({
              communicationType: 'SHIPPING_DOCS',
              recipientType: contact.docsRecipientType || 'TO',
              enabled: true,
            });
          }

          return {
            ...(contact.id && { id: contact.id }), // Include ID if editing existing contact
            firstName: contact.firstName,
            lastName: contact.lastName,
            email: contact.email,
            phone: contact.phone || undefined,
            title: contact.title || undefined,
            isPrimary: contact.isPrimary || false,
            communicationPreferences,
          };
        });

      const response = await fetch(endpoint, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
        },
        body: JSON.stringify({
          name: values.name,
          shortCode: values.shortCode,
          shortName: values.shortName || undefined,
          type: values.type,
          address: values.address || undefined,
          country: values.country || undefined,
          status: values.status,
          contacts,
          ...(values.type === 'SUPPLIER' && {
            paymentMethod: values.paymentMethod,
            needsCommissionInvoice: values.needsCommissionInvoice,
            commissionWithheldFromPayment: values.commissionWithheldFromPayment,
          }),
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to save company');
      }

      message.success(`Company ${mode === 'edit' ? 'updated' : 'created'} successfully!`);
      onClose();
      
    } catch (error: any) {
      console.error('Failed to save company:', error);
      message.error(error.message || `Failed to ${mode === 'edit' ? 'update' : 'create'} company`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const modalTitle = mode === 'edit' 
    ? `Edit ${company?.name || 'Company'}`
    : 'Create New Company';

  return (
    <Modal
      title={modalTitle}
      open={visible}
      onCancel={onClose}
      footer={null}
      width={800}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        style={{ marginTop: '16px' }}
      >
        {/* Basic Information */}
        <div style={{ marginBottom: '24px' }}>
          <h3 className="text-h3" style={{ marginBottom: '16px' }}>Basic Information</h3>
          
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '16px' }}>
            <Form.Item
              name="name"
              label="Company Name"
              rules={[{ required: true, message: 'Company name is required' }]}
            >
              <Input placeholder="Enter company name" size="large" />
            </Form.Item>

            <Form.Item
              name="shortName"
              label="Short Name"
              tooltip="A shorter display name (e.g., 'Cal Nut' instead of 'California Nut Company')"
            >
              <Input placeholder="e.g., Cal Nut" size="large" />
            </Form.Item>

            <Form.Item
              name="shortCode"
              label="Code"
              rules={[
                { required: true, message: 'Code is required' },
                {
                  pattern: /^[A-Z]{2,3}$/,
                  message: 'Must be 2-3 uppercase letters'
                }
              ]}
              normalize={(value) => value?.toUpperCase()}
            >
              <Input
                placeholder="e.g., AN"
                maxLength={3}
                size="large"
                style={{ textTransform: 'uppercase' }}
              />
            </Form.Item>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <Form.Item
              name="type"
              label="Company Type"
              rules={[{ required: true, message: 'Company type is required' }]}
            >
              <Select placeholder="Select type" size="large">
                <Option value="SUPPLIER">Supplier</Option>
                <Option value="BUYER">Buyer</Option>
                <Option value="BOTH">Supplier & Buyer</Option>
              </Select>
            </Form.Item>

            <Form.Item
              name="status"
              label="Status"
              rules={[{ required: true, message: 'Status is required' }]}
            >
              <Select placeholder="Select status" size="large">
                <Option value="ACTIVE">Active</Option>
                <Option value="INACTIVE">Inactive</Option>
              </Select>
            </Form.Item>
          </div>
        </div>

        {/* Payment Rules - Only for Suppliers */}
        <Form.Item noStyle shouldUpdate={(prev, curr) => prev.type !== curr.type}>
          {({ getFieldValue }) => {
            const companyType = getFieldValue('type');
            if (companyType !== 'SUPPLIER') return null;
            
            return (
              <div style={{ marginBottom: '24px' }}>
                <h3 className="text-h3" style={{ marginBottom: '16px' }}>Payment Rules</h3>
                
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <Form.Item
                    name="paymentMethod"
                    label="Payment Method"
                    tooltip="How this supplier typically pays commissions"
                  >
                    <Select placeholder="Select payment method" size="large">
                      <Option value="CHECK">Check</Option>
                      <Option value="WIRE">Wire Transfer</Option>
                      <Option value="ACH">ACH</Option>
                    </Select>
                  </Form.Item>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', paddingTop: '30px' }}>
                    <Form.Item
                      name="needsCommissionInvoice"
                      valuePropName="checked"
                      style={{ margin: 0 }}
                    >
                      <Checkbox>Needs Commission Invoice</Checkbox>
                    </Form.Item>
                    
                    <Form.Item
                      name="commissionWithheldFromPayment"
                      valuePropName="checked"
                      style={{ margin: 0 }}
                    >
                      <Checkbox>Commission Withheld from Payment</Checkbox>
                    </Form.Item>
                  </div>
                </div>
                
                <div style={{ 
                  marginTop: '8px', 
                  padding: '12px', 
                  background: '#f5f5f5', 
                  borderRadius: '6px',
                  fontSize: '13px',
                  color: '#666'
                }}>
                  <strong>Note:</strong> Suppliers that don't need commission invoices won't appear in the 
                  "Arrived Without Commission Invoice" dashboard widget.
                </div>
              </div>
            );
          }}
        </Form.Item>

        {/* Contacts Information */}
        <div style={{ marginBottom: '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h3 className="text-h3">Contacts</h3>
          </div>

          <Form.List name="contacts">
            {(fields, { add, remove }) => (
              <>
                {fields.map((field, index) => (
                  <Card
                    key={field.key}
                    size="small"
                    title={
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span>Contact {index + 1}</span>
                        <Space>
                          <Form.Item
                            {...field}
                            name={[field.name, 'isPrimary']}
                            valuePropName="checked"
                            style={{ margin: 0 }}
                          >
                            <Radio.Group
                              onChange={(e) => {
                                if (e.target.value) {
                                  // Uncheck all other primary flags
                                  const contacts = form.getFieldValue('contacts');
                                  contacts.forEach((_: any, i: number) => {
                                    if (i !== index) {
                                      form.setFieldValue(['contacts', i, 'isPrimary'], false);
                                    }
                                  });
                                  form.setFieldValue(['contacts', index, 'isPrimary'], true);
                                }
                              }}
                            >
                              <Radio value={true}>Primary</Radio>
                            </Radio.Group>
                          </Form.Item>
                          {fields.length > 1 && (
                            <Button
                              type="text"
                              danger
                              icon={<DeleteOutlined />}
                              onClick={() => remove(field.name)}
                            >
                              Remove
                            </Button>
                          )}
                        </Space>
                      </div>
                    }
                    style={{ marginBottom: '16px' }}
                  >
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                      <Form.Item
                        {...field}
                        name={[field.name, 'firstName']}
                        label="First Name"
                        rules={[{ required: true, message: 'First name is required' }]}
                      >
                        <Input placeholder="John" />
                      </Form.Item>

                      <Form.Item
                        {...field}
                        name={[field.name, 'lastName']}
                        label="Last Name"
                        rules={[{ required: true, message: 'Last name is required' }]}
                      >
                        <Input placeholder="Doe" />
                      </Form.Item>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                      <Form.Item
                        {...field}
                        name={[field.name, 'email']}
                        label="Email Address"
                        rules={[
                          { required: true, message: 'Email is required' },
                          { type: 'email', message: 'Please enter a valid email' }
                        ]}
                      >
                        <Input placeholder="john.doe@company.com" />
                      </Form.Item>

                      <Form.Item
                        {...field}
                        name={[field.name, 'phone']}
                        label="Phone Number"
                      >
                        <Input placeholder="+1 (555) 123-4567" />
                      </Form.Item>
                    </div>

                    <Form.Item
                      {...field}
                      name={[field.name, 'title']}
                      label="Job Title"
                    >
                      <Input placeholder="Sales Manager" />
                    </Form.Item>

                    <Form.Item
                      label="Email Notifications"
                      extra="Select which types of emails this contact should receive and whether they should be TO or CC"
                    >
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        {/* Contracts */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                          <span style={{ width: '200px', fontWeight: 500 }}>Contract Documents:</span>
                          <Form.Item
                            {...field}
                            name={[field.name, 'receiveContracts']}
                            valuePropName="checked"
                            style={{ margin: 0 }}
                          >
                            <Checkbox>Receive</Checkbox>
                          </Form.Item>
                          <Form.Item
                            {...field}
                            name={[field.name, 'contractsRecipientType']}
                            style={{ margin: 0 }}
                          >
                            <Radio.Group size="small">
                              <Radio value="TO">TO</Radio>
                              <Radio value="CC">CC</Radio>
                            </Radio.Group>
                          </Form.Item>
                        </div>

                        {/* Shipping Instructions */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                          <span style={{ width: '200px', fontWeight: 500 }}>Shipping Instructions (SI):</span>
                          <Form.Item
                            {...field}
                            name={[field.name, 'receiveShippingInstructions']}
                            valuePropName="checked"
                            style={{ margin: 0 }}
                          >
                            <Checkbox>Receive</Checkbox>
                          </Form.Item>
                          <Form.Item
                            {...field}
                            name={[field.name, 'siRecipientType']}
                            style={{ margin: 0 }}
                          >
                            <Radio.Group size="small">
                              <Radio value="TO">TO</Radio>
                              <Radio value="CC">CC</Radio>
                            </Radio.Group>
                          </Form.Item>
                        </div>

                        {/* Booking Requests */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                          <span style={{ width: '200px', fontWeight: 500 }}>Booking Requests:</span>
                          <Form.Item
                            {...field}
                            name={[field.name, 'receiveBookingRequests']}
                            valuePropName="checked"
                            style={{ margin: 0 }}
                          >
                            <Checkbox>Receive</Checkbox>
                          </Form.Item>
                          <Form.Item
                            {...field}
                            name={[field.name, 'bookingRecipientType']}
                            style={{ margin: 0 }}
                          >
                            <Radio.Group size="small">
                              <Radio value="TO">TO</Radio>
                              <Radio value="CC">CC</Radio>
                            </Radio.Group>
                          </Form.Item>
                        </div>

                        {/* Shipping Documents */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                          <span style={{ width: '200px', fontWeight: 500 }}>Shipping Documents:</span>
                          <Form.Item
                            {...field}
                            name={[field.name, 'receiveShippingDocs']}
                            valuePropName="checked"
                            style={{ margin: 0 }}
                          >
                            <Checkbox>Receive</Checkbox>
                          </Form.Item>
                          <Form.Item
                            {...field}
                            name={[field.name, 'docsRecipientType']}
                            style={{ margin: 0 }}
                          >
                            <Radio.Group size="small">
                              <Radio value="TO">TO</Radio>
                              <Radio value="CC">CC</Radio>
                            </Radio.Group>
                          </Form.Item>
                        </div>
                      </div>
                    </Form.Item>

                    {/* Hidden field to store contact ID for edits */}
                    <Form.Item
                      {...field}
                      name={[field.name, 'id']}
                      hidden
                    >
                      <Input />
                    </Form.Item>
                  </Card>
                ))}

                <Button
                  type="dashed"
                  onClick={() => add({
                    firstName: '',
                    lastName: '',
                    email: '',
                    phone: '',
                    title: '',
                    isPrimary: false,
                    receiveContracts: false,
                    contractsRecipientType: 'TO',
                    receiveShippingInstructions: false,
                    siRecipientType: 'TO',
                    receiveBookingRequests: false,
                    bookingRecipientType: 'TO',
                    receiveShippingDocs: false,
                    docsRecipientType: 'TO',
                  })}
                  block
                  icon={<PlusOutlined />}
                >
                  Add Another Contact
                </Button>
              </>
            )}
          </Form.List>
        </div>

        {/* Address Information */}
        <div style={{ marginBottom: '24px' }}>
          <h3 className="text-h3" style={{ marginBottom: '16px' }}>Address Information</h3>
          
          <Form.Item
            name="address"
            label="Address"
            extra="Enter the complete address (street, city, state/province, postal code)"
          >
            <TextArea 
              placeholder="e.g., 123 Main Street, Los Angeles, CA 90210&#10;or&#10;Tokyo Tower, 4-2-8 Shibakoen, Minato City, Tokyo 105-0011"
              rows={3}
              size="large"
            />
          </Form.Item>

          <Form.Item
            name="country"
            label="Country"
          >
            <Select 
              placeholder="Select country"
              size="large"
              showSearch
              optionFilterProp="children"
            >
              {countries.map(country => (
                <Option key={country} value={country}>{country}</Option>
              ))}
            </Select>
          </Form.Item>
        </div>

        {/* Form Actions */}
        <div style={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '12px',
          marginTop: '32px',
          paddingTop: '16px',
          borderTop: '1px solid var(--border-default)'
        }}>
          <Button onClick={onClose} size="large">
            Cancel
          </Button>
          
          <Button 
            type="primary"
            htmlType="submit"
            loading={isSubmitting}
            size="large"
          >
            {mode === 'edit' ? 'Update Company' : 'Create Company'}
          </Button>
        </div>
      </Form>
    </Modal>
  );
};

export default CompanyManagementModal;